/**
 * Vuex Store 入口文件
 * @module store
 * @description 配置并创建 Vuex Store 实例，自动加载所有模块
 */

import Vue from 'vue';
import Vuex from 'vuex';

/**
 * 注册 Vuex 插件
 */
Vue.use(Vuex);

/**
 * 获取 modules 目录下所有 Vuex 模块文件
 * @see https://webpack.js.org/guides/dependency-management/#requirecontext
 * @type {require.context}
 */
const modulesFiles = require.context('./modules', true, /\.js$/);

/**
 * 自动加载所有 Vuex 模块
 * @description 无需手动 import 每个模块，该函数会自动加载 modules 目录下所有 .js 文件
 * @type {Object<string, Object>} 包含所有 Vuex 模块的对象，键为模块名
 */
const modules = modulesFiles.keys().reduce((modules, modulePath) => {
  // 将 './app.js' 转换为 'app' 作为模块名
  const moduleName = modulePath.replace(/^\.\/(.*)\.\w+$/, '$1');
  // 获取模块内容
  const value = modulesFiles(modulePath);
  // 将模块添加到模块对象中
  modules[moduleName] = value.default;
  return modules;
}, {});

/**
 * Vuex Store 实例
 * @type {Vuex.Store}
 */
const store = new Vuex.Store({
  /**
   * 根级 state，用于全局共享的数据
   * @type {Object<string, any>}
   * @property {string} publicPath - 静态资源公共路径
   */
  state: {
    publicPath: process.env.BASE_URL, // 静态资源公共路径
  },

  /**
   * 全局 Getters，用于从 state 派生出新数据
   * @type {Object<string, Function>}
   */
  getters: {},

  /**
   * 全局 Mutations，用于同步修改 state
   * @type {Object<string, Function>}
   */
  mutations: {},

  /**
   * 全局 Actions，用于异步操作或组合多个 mutations
   * @type {Object<string, Function>}
   */
  actions: {},

  /**
   * 注册所有 Vuex 模块
   * @type {Object<string, Object>}
   */
  modules,
});

/**
 * 导出 Vuex Store 实例
 * @exports
 */
export default store;
